import {
  ZIN_DISPLAY_KEY_STORY_CITY_PAGE,
  ZIN_KEY_STORY_CITY_PAGE
} from "@website/accommodation-ui/constant";
import { HotelCitiesMainContent } from "@website/accommodation-ui/hotel-cities-main-content";
import { HotelFaq } from "@website/accommodation-ui/hotel-faq";
import { HotelFilter } from "@website/accommodation-ui/hotel-filter";
import { HotelInboundLinks } from "@website/accommodation-ui/hotel-inbound-links";
import { HotelListHeader } from "@website/accommodation-ui/hotel-list-header";
import { HotelPagination } from "@website/accommodation-ui/hotel-pagination";
import { HotelSearch } from "@website/accommodation-ui/hotel-search";
import { HotelSeoContent } from "@website/accommodation-ui/hotel-seo-content";
import { HotelShortVideos } from "@website/accommodation-ui/hotel-short-videos";
import { HotelUpdateSearchModal } from "@website/accommodation-ui/hotel-update-search-modal";
import { MaterialIcon } from "@website/icons/material";
import { Breadcrumb } from "@website/ui/breadcrumb";
import { getChannel } from "@website/utils/channel/get-channel";
import { isChannel } from "@website/utils/channel/helpers";
import { NotFoundComponent } from "@website/widget-v2/not-found";
import { BannerSlide } from "@website/widget/banner-slide";
import { Footer } from "@website/widget/footer";
import { HeaderWrapper } from "@website/widget/header-wrapper";
import { AnimatedButton } from "@website/widget/search-map/animatedButton";
import { Telesales } from "@website/widget/telesales";
import toString from "lodash-es/toString";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { useCityPage } from "./index.hook";
import { CityPagePropertiesType } from "./index.types";

const staticCitiesMetaTags = {
  تهران: [
    {
      name: "geo.region",
      content: "IR-TH"
    },
    {
      name: "geo.placename",
      content: "Tehran"
    },
    {
      name: "geo.position",
      content: "35.6892;51.3890"
    },
    {
      name: "ICBM",
      content: "35.6892,51.3890"
    }
  ],
  مشهد: [
    {
      name: "geo.region",
      content: "IR-KH"
    },
    {
      name: "geo.placename",
      content: "Mashhad"
    },
    {
      name: "geo.position",
      content: "36.2605;59.6168"
    },
    {
      name: "ICBM",
      content: "36.2605,59.6168"
    }
  ],
  تبریز: [
    {
      name: "geo.region",
      content: "IR-EB"
    },
    {
      name: "geo.placename",
      content: "Tabriz"
    },
    {
      name: "geo.position",
      content: "38.0962;46.2738"
    },
    {
      name: "ICBM",
      content: "38.0962,46.2738"
    }
  ],
  اصفهان: [
    {
      name: "geo.region",
      content: "IR-ES"
    },
    {
      name: "geo.placename",
      content: "Isfahan"
    },
    {
      name: "geo.position",
      content: "32.6539;51.6660"
    },
    {
      name: "ICBM",
      content: "32.6539,51.6660"
    }
  ],
  شیراز: [
    {
      name: "geo.region",
      content: "IR-FR"
    },
    {
      name: "geo.placename",
      content: "Shiraz"
    },
    {
      name: "geo.position",
      content: "29.5918;52.5837"
    },
    {
      name: "ICBM",
      content: "29.5918,52.5837"
    }
  ],
  کیش: [
    {
      name: "geo.region",
      content: "IR-HO"
    },
    {
      name: "geo.placename",
      content: "Kish"
    },
    {
      name: "geo.position",
      content: "26.5320;53.9800"
    },
    {
      name: "ICBM",
      content: "26.5320,53.9800"
    }
  ],
  قشم: [
    {
      name: "geo.region",
      content: "IR-HO"
    },
    {
      name: "geo.placename",
      content: "Qeshm"
    },
    {
      name: "geo.position",
      content: "26.9584;56.2719"
    },
    {
      name: "ICBM",
      content: "29.5918,52.5837"
    }
  ],
  یزد: [
    {
      name: "geo.region",
      content: "IR-YZ"
    },
    {
      name: "geo.placename",
      content: "Yazd"
    },
    {
      name: "geo.position",
      content: "31.8974;54.3569"
    },
    {
      name: "ICBM",
      content: "31.8974,54.3569"
    }
  ]
};

const Index: NextPage<CityPagePropertiesType> = (properties) => {
  if (!properties?.cityData) {
    return (
      <div>
        <HeaderWrapper showAuth title="هتل داخلی" showDropShadow={false} />
        <NotFoundComponent />
      </div>
    );
  }

  const router = useRouter();
  const {
    modalProps,
    searchProperties,
    citiesFilterProperties,
    hotelListHeaderProps,
    hotelPaginationProps,
    hotelCitiesMainContentProps,
    hotelBreadcrumbsProperties,
    hotelSeoContentProps,
    hotelFaqProps,
    hotelInboundLinksProps,
    telesaleProperties,
    bannersProperties,
    onMapClick,
    metaData
  } = useCityPage(properties);

  useEffect(() => {
    const element = document.querySelector("#city-hotel-domestic");
    element?.scrollIntoView?.({
      behavior: "smooth",
      block: "start"
    });
  }, []);

  const zinStoryTag = useMemo(
    () =>
      (router?.query?.cityTitle as string)
        ?.toLowerCase()
        .replaceAll("-", "_")
        .replaceAll(/[^\p{L}\p{N}\s]/gu, "")
        .replaceAll(" ", "_"),
    [router?.query?.cityTitle]
  );

  return (
    <>
      <Head>
        {staticCitiesMetaTags?.[toString(router.query.cityTitle)]?.map(
          ({ name, content }) => (
            <meta content={content} key={content} name={name} />
          )
        )}
        {isChannel(["snapp-ir"]) ? (
          <title>
            {`رزرو هتل ${properties?.cityData?.title} با بیشترین تخفیف و پرداخت قسطی - اسنپ`}
          </title>
        ) : (
          <title>{metaData.title}</title>
        )}
        <meta name="description" content={metaData.description} />
      </Head>
      <div className="bg-dim-background">
        <HotelUpdateSearchModal {...modalProps} />
        <HeaderWrapper
          showAuth
          showDropShadow={false}
          showTravelServices={getChannel() !== "ads"}
          title="هتل داخلی"
        />
        <div className="bg-outline-thin h-px w-full" />
        <HotelSearch {...searchProperties} />
        <div className="flex flex-col gap-5 px-6 lg:py-5 xl:mx-auto xl:max-w-[1200px] xl:px-0">
          <div className="hidden lg:block">
            <Breadcrumb {...hotelBreadcrumbsProperties} />
          </div>
          <div className="relative flex">
            <div className="p-0">
              {bannersProperties?.bannerSlideList?.length > 0 && (
                <div className="hidden w-[312px] pb-4 xl:block">
                  <BannerSlide
                    className="p-0"
                    list={bannersProperties?.bannerSlideList}
                  />
                </div>
              )}
              <HotelFilter {...citiesFilterProperties} />
            </div>
            <main className="flex min-w-0 shrink grow flex-col gap-5 lg:gap-4">
              <HotelListHeader {...hotelListHeaderProps} />
              {/*{isAdChannel ? null : <HotelBanner />}*/}
              {isChannel(["jek"]) && (
                <HotelShortVideos
                  className="bg-on-color-high-emphasis border-y-outline-thin -mx-6 -mt-5 border px-4 lg:m-0 lg:hidden"
                  displayType={"STORY"}
                  displayId={ZIN_DISPLAY_KEY_STORY_CITY_PAGE}
                  zinKey={ZIN_KEY_STORY_CITY_PAGE}
                  zinTags={zinStoryTag}
                />
              )}
              <HotelPagination {...hotelPaginationProps}>
                <HotelCitiesMainContent {...hotelCitiesMainContentProps} />
              </HotelPagination>
              <AnimatedButton
                className="bottom-[20px] self-center md:bottom-6 md:!self-auto xl:!hidden"
                icon={<MaterialIcon name="map" variant="outlined" />}
                onClick={onMapClick}
                text="نمایش هتل‌ها روی نقشه"
              />
            </main>
          </div>
        </div>
        <div className="w-full px-6 py-2 xl:mx-auto xl:max-w-[1200px] xl:px-0">
          {!isChannel(["snapp-ir"]) && (
            <HotelSeoContent {...hotelSeoContentProps} />
          )}
          {!isChannel(["snapp-ir"]) && <HotelFaq {...hotelFaqProps} />}
          <HotelInboundLinks {...hotelInboundLinksProps} />
        </div>
        {!isChannel(["b2b"]) && (
          <Telesales {...telesaleProperties} className="!left-6 !right-auto" />
        )}
        <Footer />
      </div>
    </>
  );
};

export default Index;

export { getServerSideProps } from "./index.server";
